import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle, XCircle, ArrowRight, Spinner } from "@phosphor-icons/react";
import { getStripeStatus, verifyPaystack, getOrder } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { formatGHS, formatUSD } from "@/lib/format";

export default function OrderSuccessPage() {
  const [params] = useSearchParams();
  const provider = params.get("provider");
  const sessionId = params.get("session_id");
  const reference = params.get("reference") || params.get("trxref");
  const orderId = params.get("order_id");

  const { clear } = useCart();
  const [status, setStatus] = useState("checking"); // checking | success | failed | timeout
  const [order, setOrder] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let timer;

    const poll = async (attempt = 0) => {
      if (cancelled) return;
      const max = 8;
      try {
        let result;
        if (provider === "stripe" && sessionId) {
          result = await getStripeStatus(sessionId);
        } else if (provider === "paystack" && reference) {
          result = await verifyPaystack(reference);
        } else if (provider === "paystack" && orderId) {
          // Fallback path: paystack callback may only return order_id
          result = { payment_status: "pending" };
        }

        if (cancelled) return;
        setPollCount(attempt + 1);

        if (result?.payment_status === "paid") {
          setStatus("success");
          clear();
          if (result.order_id || orderId) {
            try {
              const o = await getOrder(result.order_id || orderId);
              setOrder(o);
            } catch (err) {
              console.error("Failed to load order details after payment:", err);
            }
          }
        } else if (result?.payment_status === "failed" || result?.status === "expired") {
          setStatus("failed");
        } else if (attempt + 1 >= max) {
          setStatus("timeout");
        } else {
          timer = setTimeout(() => poll(attempt + 1), 2500);
        }
      } catch {
        if (attempt + 1 >= max) setStatus("timeout");
        else timer = setTimeout(() => poll(attempt + 1), 2500);
      }
    };

    poll(0);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="order-success-page" className="bg-[#0a0a0a] text-white min-h-screen flex items-center justify-center">
      <div className="max-w-2xl w-full px-6 md:px-10 py-20 text-center">
        {status === "checking" && (
          <div data-testid="order-checking">
            <Spinner size={48} className="text-[#FFC107] mx-auto mb-6 animate-spin" />
            <h1 className="font-display text-4xl font-black uppercase mb-3">Verifying payment…</h1>
            <p className="text-zinc-400">Hold tight, this takes a few seconds (attempt {pollCount + 1}).</p>
          </div>
        )}
        {status === "success" && (
          <div data-testid="order-success">
            <CheckCircle size={64} weight="fill" className="text-[#FFC107] mx-auto mb-6" />
            <h1 className="font-display text-4xl sm:text-5xl font-black uppercase mb-3">
              Order locked in.
            </h1>
            <p className="text-zinc-300 max-w-md mx-auto">
              Thank you! Your payment is confirmed. We&apos;ll WhatsApp you with delivery and installation details.
            </p>
            {order && (
              <div className="mt-10 text-left border border-white/10 bg-[#141414] p-6">
                <p className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Order ID</p>
                <p className="font-mono-accent text-[#FFC107] break-all">{order.id}</p>
                <div className="border-t border-white/10 my-4" />
                <ul className="space-y-2 text-sm">
                  {order.items?.map((it, i) => (
                    <li key={it.product_id || i} className="flex justify-between">
                      <span>{it.name} × {it.quantity}</span>
                      <span className="font-mono-accent text-zinc-300">
                        {order.currency === "GHS" ? formatGHS(it.subtotal) : formatUSD(it.subtotal)}
                      </span>
                    </li>
                  ))}
                </ul>
                <div className="border-t border-white/10 my-4" />
                <div className="flex justify-between font-display font-bold">
                  <span>Total</span>
                  <span className="text-[#FFC107]">
                    {order.currency === "GHS" ? formatGHS(order.total) : formatUSD(order.total)}
                  </span>
                </div>
              </div>
            )}
            <Link
              to="/"
              data-testid="back-home-btn"
              className="mt-10 inline-flex items-center gap-2 h-12 px-6 bg-[#FFC107] hover:bg-[#FFB300] text-black font-black uppercase tracking-wider text-sm"
            >
              Back home <ArrowRight size={18} weight="bold" />
            </Link>
          </div>
        )}
        {status === "failed" && (
          <div data-testid="order-failed">
            <XCircle size={64} weight="fill" className="text-[#D32F2F] mx-auto mb-6" />
            <h1 className="font-display text-4xl font-black uppercase mb-3">Payment failed</h1>
            <p className="text-zinc-400">No money was taken. Please try checkout again or use a different method.</p>
            <Link to="/checkout" className="mt-8 inline-block h-12 px-6 border border-white/20 hover:border-[#FFC107] hover:text-[#FFC107] font-bold uppercase tracking-wider text-sm leading-[3rem]">
              Try again
            </Link>
          </div>
        )}
        {status === "timeout" && (
          <div data-testid="order-timeout">
            <Spinner size={64} className="text-[#FFC107] mx-auto mb-6" />
            <h1 className="font-display text-4xl font-black uppercase mb-3">Still confirming…</h1>
            <p className="text-zinc-400">Your payment may still be processing. We&apos;ll email you once it&apos;s confirmed.</p>
            <Link to="/" className="mt-8 inline-block h-12 px-6 border border-white/20 hover:border-[#FFC107] hover:text-[#FFC107] font-bold uppercase tracking-wider text-sm leading-[3rem]">
              Back home
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
